import { useEffect, useMemo, useState } from 'react';
import { X, AlertTriangle, Check, Loader2, User } from 'lucide-react';
import {
  useAppointmentCandidates,
  useAppointmentCatalog,
  useCreateAppointment,
} from '@/api/resources';
import { Button } from '@/components/ui/Button';
import { SKILL_LABELS } from './AppointmentsPage';

const DURATIONS = [5, 10, 15, 20, 30, 45, 60, 90];

/**
 * Création d'un RDV métier.
 *
 * La vérification de compétence est faite **pendant la saisie** : dès que le
 * type, la date, l'heure et la durée sont posés, on interroge le backend pour
 * savoir qui est présent, qualifié et libre. S'il n'y a personne, le formulaire
 * passe en rouge et le seul moyen de valider est de désigner quelqu'un à titre
 * exceptionnel, en motivant la décision.
 */
export default function NewAppointmentModal({
  dayKey,
  time,
  onClose,
}: {
  dayKey: string;
  time: string;
  onClose: () => void;
}) {
  const { data: catalog } = useAppointmentCatalog();

  const [catalogItemId, setCatalogItemId] = useState('');
  const [date, setDate] = useState(dayKey);
  const [start, setStart] = useState(time);
  const [duration, setDuration] = useState(15);
  const [durationTouched, setDurationTouched] = useState(false);
  const [patientName, setPatientName] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [notes, setNotes] = useState('');

  // Désignation exceptionnelle
  const [forcedEmployeeId, setForcedEmployeeId] = useState('');
  const [forcedReason, setForcedReason] = useState('');

  const create = useCreateAppointment();
  const [error, setError] = useState<string | null>(null);

  const item = useMemo(
    () => catalog?.find((c) => c.id === catalogItemId) ?? null,
    [catalog, catalogItemId]
  );

  // Première prestation du catalogue par défaut.
  useEffect(() => {
    if (!catalogItemId && catalog?.length) setCatalogItemId(catalog[0].id);
  }, [catalog, catalogItemId]);

  // La durée suit celle de la prestation tant que l'utilisateur n'y a pas touché.
  useEffect(() => {
    if (item && !durationTouched) setDuration(item.defaultDurationMinutes);
  }, [item, durationTouched]);

  // Prestations regroupées par catégorie, dans l'ordre du catalogue.
  const grouped = useMemo(() => {
    const groups: { category: string; items: NonNullable<typeof catalog> }[] = [];
    for (const c of catalog ?? []) {
      const key = c.category ?? 'Autres';
      let g = groups.find((x) => x.category === key);
      if (!g) groups.push((g = { category: key, items: [] }));
      g.items.push(c);
    }
    return groups;
  }, [catalog]);

  /**
   * Heure locale pharmacie transportée en UTC — même convention que le
   * planning (dates à minuit UTC, horaires en chaînes « HH:mm »).
   */
  const startAt = useMemo(() => `${date}T${start}:00.000Z`, [date, start]);

  const params = useMemo(
    () =>
      catalogItemId && date && start
        ? { catalogItemId, startAt, durationMinutes: duration }
        : null,
    [catalogItemId, startAt, duration, date, start]
  );
  const { data: candidates, isFetching } = useAppointmentCandidates(params);

  const eligible = candidates?.eligible ?? [];
  const fallback = candidates?.fallback ?? [];
  const pinned = !!candidates?.pinnedEmployeeId;
  // Une prestation nominative est contrainte au même titre qu'une compétence.
  const constrained = !!candidates?.requiredSkill || pinned;
  const noOneQualified = constrained && eligible.length === 0;

  // Le forçage est obligatoire quand personne n'est qualifié, et facultatif
  // sinon (le titulaire peut vouloir désigner un non-qualifié malgré tout).
  const forcedIsUnqualified =
    !!forcedEmployeeId && !eligible.some((e) => e.id === forcedEmployeeId);
  const reasonRequired = forcedIsUnqualified;
  const reasonOk = !reasonRequired || forcedReason.trim().length >= 5;

  const canSubmit =
    !!catalogItemId &&
    !!date &&
    !!start &&
    reasonOk &&
    (!noOneQualified || !!forcedEmployeeId) &&
    !create.isPending;

  async function submit() {
    setError(null);
    try {
      await create.mutateAsync({
        catalogItemId,
        startAt,
        durationMinutes: duration,
        patientName: patientName || undefined,
        patientPhone: patientPhone || undefined,
        notes: notes || undefined,
        forcedEmployeeId: forcedEmployeeId || undefined,
        forcedReason: reasonRequired ? forcedReason.trim() : undefined,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La création a échoué.');
    }
  }

  const field = 'w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-[13px]';
  const label = 'text-[11px] font-medium text-slate-500 mb-1 block';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"
        >
          <X size={16} />
        </button>

        <h2 className="text-[15px] font-semibold text-slate-900">Nouveau rendez-vous</h2>
        <p className="text-[12px] text-slate-500 mb-4">
          La compétence requise est vérifiée sur le créneau exact.
        </p>

        <div className="space-y-3">
          <div>
            <label className={label}>Prestation</label>
            <select
              value={catalogItemId}
              onChange={(e) => {
                setCatalogItemId(e.target.value);
                setDurationTouched(false); // la durée repart de la prestation
                setForcedEmployeeId('');
              }}
              className={field}
            >
              {grouped.map((g) => (
                <optgroup key={g.category} label={g.category}>
                  {g.items.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <div className="text-[11px] text-slate-500 mt-1">
              {item?.requiredSkill
                ? `Compétence requise : ${SKILL_LABELS[item.requiredSkill] ?? item.requiredSkill}`
                : 'Aucune compétence requise'}
              {item?.defaultEmployee && (
                <span className="inline-flex items-center gap-1 ml-2 text-slate-600">
                  <User size={11} />
                  Réservée à {item.defaultEmployee.firstName} {item.defaultEmployee.lastName}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className={label}>Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={field}
              />
            </div>
            <div>
              <label className={label}>Heure</label>
              <input
                type="time"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className={field}
              />
            </div>
            <div>
              <label className={label}>Durée</label>
              <select
                value={duration}
                onChange={(e) => {
                  setDuration(Number(e.target.value));
                  setDurationTouched(true);
                }}
                className={field}
              >
                {DURATIONS.map((d) => (
                  <option key={d} value={d}>
                    {d} min
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ---- Verdict de compétence ---- */}
          <div
            className={`rounded-lg border px-3 py-2.5 ${
              isFetching
                ? 'border-slate-200 bg-slate-50'
                : noOneQualified
                ? 'border-red-300 bg-red-50'
                : constrained
                ? 'border-emerald-300 bg-emerald-50'
                : 'border-slate-200 bg-slate-50'
            }`}
          >
            {isFetching ? (
              <div className="flex items-center gap-2 text-[12px] text-slate-500">
                <Loader2 size={13} className="animate-spin" />
                Vérification des compétences…
              </div>
            ) : !constrained ? (
              <div className="text-[12px] text-slate-600">
                Cette prestation n'exige aucune compétence particulière — n'importe qui peut la
                prendre.
              </div>
            ) : noOneQualified ? (
              <div>
                <div className="flex items-center gap-1.5 text-[12px] font-semibold text-red-800">
                  <AlertTriangle size={13} />
                  {pinned && !candidates!.requiredSkill
                    ? "La personne à qui cette prestation est réservée n'est pas disponible"
                    : `Aucune personne qualifiée « ${
                        SKILL_LABELS[candidates!.requiredSkill!]
                      } » sur ce créneau`}
                </div>
                <div className="text-[11px] text-red-700 mt-1">
                  Vous pouvez quand même valider en désignant quelqu'un à titre exceptionnel,
                  avec un commentaire.
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-1.5 text-[12px] font-semibold text-emerald-800">
                  <Check size={13} />
                  {eligible.length} personne{eligible.length > 1 ? 's' : ''} disponible
                  {eligible.length > 1 ? 's' : ''}
                  {candidates!.requiredSkill
                    ? ` « ${SKILL_LABELS[candidates!.requiredSkill]} »`
                    : ''}
                </div>
                <div className="text-[11px] text-emerald-700 mt-1">
                  {eligible.map((e) => `${e.firstName} ${e.lastName[0]}.`).join(', ')}
                </div>
              </div>
            )}
          </div>

          {/* ---- Affectation ---- */}
          <div>
            <label className={label}>
              {noOneQualified ? 'Désigner à titre exceptionnel' : 'Affectation'}
            </label>
            <select
              value={forcedEmployeeId}
              onChange={(e) => setForcedEmployeeId(e.target.value)}
              className={field}
            >
              <option value="">
                {noOneQualified ? '— Choisir une personne —' : 'Automatique (premier qualifié)'}
              </option>
              {eligible.length > 0 && (
                <optgroup label="Disponibles pour cette prestation">
                  {eligible.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.firstName} {e.lastName}
                    </option>
                  ))}
                </optgroup>
              )}
              {fallback.length > 0 && (
                <optgroup
                  label={pinned ? 'Présents (hors réservation)' : 'Présents mais non qualifiés'}
                >
                  {fallback.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.firstName} {e.lastName}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {noOneQualified && fallback.length === 0 && (
              <div className="text-[11px] text-red-600 mt-1">
                Personne n'est présent sur ce créneau. Vérifiez le planning ou changez d'horaire.
              </div>
            )}
          </div>

          {reasonRequired && (
            <div>
              <label className={label}>Motif de la désignation exceptionnelle *</label>
              <textarea
                value={forcedReason}
                onChange={(e) => setForcedReason(e.target.value)}
                rows={2}
                placeholder="Ex. : formation TROD en cours, supervision par le titulaire."
                className={field}
              />
              {forcedReason.trim().length > 0 && forcedReason.trim().length < 5 && (
                <div className="text-[11px] text-red-600 mt-1">
                  Le motif doit faire au moins 5 caractères.
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={label}>Patient</label>
              <input
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
                className={field}
              />
            </div>
            <div>
              <label className={label}>Téléphone</label>
              <input
                value={patientPhone}
                onChange={(e) => setPatientPhone(e.target.value)}
                className={field}
              />
            </div>
          </div>

          <div>
            <label className={label}>Remarque</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className={field} />
          </div>

          {error && (
            <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="default" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {create.isPending ? 'Création…' : noOneQualified ? 'Valider malgré tout' : 'Créer'}
          </Button>
        </div>
      </div>
    </div>
  );
}
